import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, Edit, Trash2, Calendar, Link, Copy, Users, ExternalLink, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PreOrderEventsPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [packages, setPackages] = useState([]);
  const [products, setProducts] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showLinkGalleryDialog, setShowLinkGalleryDialog] = useState(false);
  const [selectedEventForGallery, setSelectedEventForGallery] = useState(null);
  const [selectedGalleryIds, setSelectedGalleryIds] = useState([]);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    event_date: '',
    preorder_deadline: '',
    packages_available: [],
    products_available: [],
    teams: '',
    event_type: 'sports',
    allowed_background_categories: ['general']
  });

  const EVENT_TYPES = [
    { value: 'sports', label: 'Sports' },
    { value: 'school', label: 'School' },
    { value: 'portrait', label: 'Portrait' },
    { value: 'other', label: 'Other' }
  ];

  const BACKGROUND_CATEGORIES = [
    { value: 'sports', label: 'Sports' },
    { value: 'school', label: 'School' },
    { value: 'general', label: 'General/All' },
    { value: 'abstract', label: 'Abstract' }
  ];

  useEffect(() => {
    fetchEvents();
    fetchPackages();
    fetchProducts();
    fetchGalleries();
  }, []);

  const toggleDigitalOnly = async (event, enabled) => {
    try {
      const res = await axios.put(`${API}/preorder-events/${event.id}/digital-only?enabled=${enabled}`);
      setEvents(prev => prev.map(e => e.id === event.id ? { ...e, digital_only: enabled } : e));
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update event');
    }
  };

  const toggleDeliveryFees = async (event, enabled) => {
    try {
      const res = await axios.put(`${API}/preorder-events/${event.id}/delivery-fees?enabled=${enabled}`);
      setEvents(prev => prev.map(e => e.id === event.id ? { ...e, delivery_fees_enabled: enabled } : e));
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update event');
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/preorder-events`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load pre-order events');
    } finally {
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data);
    } catch (error) {
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
    }
  };

  const fetchGalleries = async () => {
    try {
      const response = await axios.get(`${API}/galleries`);
      setGalleries(response.data);
    } catch (error) {
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const resetForm = () => {
    setForm({
      name: '',
      slug: '',
      description: '',
      event_date: '',
      preorder_deadline: '',
      packages_available: [],
      products_available: [],
      teams: '',
      event_type: 'sports',
      allowed_background_categories: ['general']
    });
    setEditingEvent(null);
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setForm({
      name: event.name,
      slug: event.slug,
      description: event.description || '',
      event_date: event.event_date ? event.event_date.split('T')[0] : '',
      preorder_deadline: event.preorder_deadline ? event.preorder_deadline.split('T')[0] : '',
      packages_available: event.packages_available || [],
      products_available: event.products_available || [],
      teams: (event.teams || []).join('\n'),
      event_type: event.event_type || 'sports',
      allowed_background_categories: event.allowed_background_categories || ['general']
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.slug) {
      toast.error('Please fill in required fields');
      return;
    }

    const payload = {
      name: form.name,
      slug: form.slug,
      description: form.description,
      event_date: form.event_date || null,
      preorder_deadline: form.preorder_deadline || null,
      packages_available: form.packages_available,
      products_available: form.products_available,
      teams: form.teams.split('\n').map(t => t.trim()).filter(t => t),
      event_type: form.event_type,
      allowed_background_categories: form.allowed_background_categories
    };

    try {
      if (editingEvent) {
        await axios.put(`${API}/preorder-events/${editingEvent.id}`, payload);
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/preorder-events`, payload);
        toast.success('Event created');
      }
      setShowDialog(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await axios.delete(`${API}/preorder-events/${eventId}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleLinkGallery = async (galleryId) => {
    if (!selectedEventForGallery) return;
    try {
      await axios.put(`${API}/preorder-events/${selectedEventForGallery.id}/link-gallery?gallery_id=${galleryId}`);
      toast.success('Gallery linked to event');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to link gallery');
    }
  };

  const handleLinkMultipleGalleries = async () => {
    if (!selectedEventForGallery || selectedGalleryIds.length === 0) return;
    try {
      await axios.put(
        `${API}/preorder-events/${selectedEventForGallery.id}/link-galleries`,
        selectedGalleryIds
      );
      toast.success(`Linked ${selectedGalleryIds.length} galleries`);
      setShowLinkGalleryDialog(false);
      setSelectedEventForGallery(null);
      setSelectedGalleryIds([]);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to link galleries');
    }
  };

  const handleUnlinkGallery = async (eventId, galleryId) => {
    try {
      await axios.delete(`${API}/preorder-events/${eventId}/unlink-gallery?gallery_id=${galleryId}`);
      toast.success('Gallery unlinked');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to unlink gallery');
    }
  };

  const toggleGallerySelection = (galleryId) => {
    setSelectedGalleryIds(prev =>
      prev.includes(galleryId)
        ? prev.filter(id => id !== galleryId)
        : [...prev, galleryId]
    );
  };

  const openLinkGalleryDialog = (event) => {
    setSelectedEventForGallery(event);
    setSelectedGalleryIds(event.gallery_ids || []);
    setShowLinkGalleryDialog(true);
  };

  const getGalleryName = (galleryId) => {
    return galleries.find(g => g.id === galleryId)?.name || 'Unknown Gallery';
  };

  const copyPreOrderLink = (slug) => {
    const link = `${window.location.origin}/preorder/${slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Pre-order link copied!');
  };

  const togglePackage = (packageId) => {
    setForm(prev => ({
      ...prev,
      packages_available: prev.packages_available.includes(packageId)
        ? prev.packages_available.filter(id => id !== packageId)
        : [...prev.packages_available, packageId]
    }));
  };

  const toggleProduct = (productId) => {
    setForm(prev => ({
      ...prev,
      products_available: prev.products_available.includes(productId)
        ? prev.products_available.filter(id => id !== productId)
        : [...prev.products_available, productId]
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const getPreorderCount = (eventId) => {
    // This would need to be fetched from the backend
    return 0;
  };

  return (
    <div className="space-y-6" data-testid="preorder-events-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-stone-800">Pre-Order Events</h1>
          <p className="text-stone-600">Create events for clients to pre-order before photo sessions</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowDialog(true); }}
          className="bg-amber-500 hover:bg-amber-600"
          data-testid="create-event-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Event
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-48 bg-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : events.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <Card key={event.id} className="border-stone-200">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg text-stone-800">{event.name}</CardTitle>
                    <p className="text-sm text-stone-500 mt-1">/{event.slug}</p>
                  </div>
                  {(event.gallery_ids?.length > 0) ? (
                    <Badge className="bg-green-100 text-green-700">
                      {event.gallery_ids.length} {event.gallery_ids.length === 1 ? 'Gallery' : 'Galleries'}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-600 border-amber-300">No Galleries</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border border-purple-200 bg-purple-50/60 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-purple-900">Digital Only</p>
                    <p className="text-xs text-purple-700">
                      {event.digital_only ? 'Clients see digital products only' : 'Regular ordering (prints + digital)'}
                    </p>
                  </div>
                  <Switch
                    checked={!!event.digital_only}
                    onCheckedChange={(v) => toggleDigitalOnly(event, v)}
                    data-testid={`digital-only-toggle-${event.id}`}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border border-sky-200 bg-sky-50/60 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-sky-900">Delivery Fees</p>
                    <p className="text-xs text-sky-700">
                      {event.delivery_fees_enabled === false ? 'OFF — no delivery, parents pick up (no fees)' : 'Clients choose and pay for delivery'}
                    </p>
                  </div>
                  <Switch
                    checked={event.delivery_fees_enabled !== false}
                    onCheckedChange={(v) => toggleDeliveryFees(event, v)}
                    data-testid={`delivery-fees-toggle-${event.id}`}
                  />
                </div>

                {event.description && (
                  <p className="text-sm text-stone-600">{event.description}</p>
                )}
                
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {event.event_date && (
                    <div className="flex items-center gap-1 text-stone-600">
                      <Calendar className="h-3 w-3" />
                      <span>{new Date(event.event_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {event.preorder_deadline && (
                    <div className="text-stone-600">
                      <span className="text-xs">Deadline: </span>
                      {new Date(event.preorder_deadline).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {/* Linked Galleries */}
                {event.gallery_ids?.length > 0 && (
                  <div className="text-xs space-y-1">
                    <p className="font-medium text-stone-500">Linked Galleries:</p>
                    <div className="flex flex-wrap gap-1">
                      {event.gallery_ids.slice(0, 3).map(gid => (
                        <Badge key={gid} variant="outline" className="text-xs">
                          {getGalleryName(gid)}
                        </Badge>
                      ))}
                      {event.gallery_ids.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{event.gallery_ids.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Event Type & Background Categories */}
                <div className="flex flex-wrap gap-1 mt-2">
                  <Badge variant="outline" className="text-xs capitalize">
                    {event.event_type || 'sports'}
                  </Badge>
                  {event.allowed_background_categories?.map(cat => (
                    <Badge key={cat} className="text-xs bg-purple-100 text-purple-700">
                      BG: {cat}
                    </Badge>
                  ))}
                </div>

                <div className="text-xs text-stone-500">
                  {event.packages_available?.length || 0} packages, {event.products_available?.length || 0} products
                </div>

                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => copyPreOrderLink(event.slug)}
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copy Link
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/preorder/${event.slug}`, '_blank')}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => navigate(`/admin/preorder-events/${event.id}/upload-photos`)}
                  >
                    <Upload className="h-3 w-3 mr-1" />
                    Upload Photos
                  </Button>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => openLinkGalleryDialog(event)}
                  >
                    <Link className="h-3 w-3 mr-1" />
                    {event.gallery_ids?.length > 0 ? 'Manage Galleries' : 'Link Galleries'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(event)}
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(event.id)}
                  >
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-dashed border-2 border-stone-300">
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto text-stone-400 mb-4" />
            <h3 className="text-lg font-medium text-stone-700 mb-2">No Pre-Order Events</h3>
            <p className="text-stone-500 mb-4">Create your first event to start accepting pre-orders</p>
            <Button onClick={() => setShowDialog(true)} className="bg-amber-500 hover:bg-amber-600">
              <Plus className="h-4 w-4 mr-2" />
              Create Event
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Event Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'New Pre-Order Event'}</DialogTitle>
            <DialogDescription>
              {editingEvent ? 'Update event details' : 'Create an event for clients to pre-order packages'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Event Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Spring Soccer 2025"
                  value={form.name}
                  onChange={(e) => {
                    setForm({ 
                      ...form, 
                      name: e.target.value,
                      slug: editingEvent ? form.slug : generateSlug(e.target.value)
                    });
                  }}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">URL Slug *</Label>
                <Input
                  id="slug"
                  placeholder="spring-soccer-2025"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: generateSlug(e.target.value) })}
                  required
                />
                <p className="text-xs text-slate-500">/preorder/{form.slug || 'your-slug'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="event_date">Event/Photo Date</Label>
                <Input
                  id="event_date"
                  type="date"
                  value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="preorder_deadline">Pre-Order Deadline</Label>
                <Input
                  id="preorder_deadline"
                  type="date"
                  value={form.preorder_deadline}
                  onChange={(e) => setForm({ ...form, preorder_deadline: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description shown to clients"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="teams">Teams/Groups (one per line)</Label>
              <Textarea
                id="teams"
                placeholder="U10 Boys&#10;U12 Girls&#10;Varsity"
                value={form.teams}
                onChange={(e) => setForm({ ...form, teams: e.target.value })}
                rows={3}
              />
            </div>

            {/* Event Type and Background Categories */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="event_type">Event Type</Label>
                <Select
                  value={form.event_type}
                  onValueChange={(value) => setForm({ ...form, event_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Allowed Background Categories</Label>
                <div className="border rounded-lg p-2 space-y-1">
                  {BACKGROUND_CATEGORIES.map(cat => (
                    <div key={cat.value} className="flex items-center gap-2">
                      <Checkbox
                        id={`bg-cat-${cat.value}`}
                        checked={form.allowed_background_categories.includes(cat.value)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setForm({ ...form, allowed_background_categories: [...form.allowed_background_categories, cat.value] });
                          } else {
                            setForm({ ...form, allowed_background_categories: form.allowed_background_categories.filter(c => c !== cat.value) });
                          }
                        }}
                      />
                      <Label htmlFor={`bg-cat-${cat.value}`} className="text-sm font-normal cursor-pointer">
                        {cat.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Packages Available for Pre-Order</Label>
              {packages.length === 0 ? (
                <p className="text-sm text-slate-500">No packages available. Create packages first.</p>
              ) : (
                <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
                  {packages.map(pkg => (
                    <div key={pkg.id} className="p-2 flex items-center gap-2">
                      <Checkbox
                        id={`pkg-${pkg.id}`}
                        checked={form.packages_available.includes(pkg.id)}
                        onCheckedChange={() => togglePackage(pkg.id)}
                      />
                      <label htmlFor={`pkg-${pkg.id}`} className="flex-1 text-sm cursor-pointer">
                        {pkg.name}
                        <span className="text-slate-500 ml-2">{formatCurrency(pkg.price)}</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Individual Products Available</Label>
              {products.length === 0 ? (
                <p className="text-sm text-slate-500">No products available.</p>
              ) : (
                <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
                  {products.map(product => (
                    <div key={product.id} className="p-2 flex items-center gap-2">
                      <Checkbox
                        id={`prod-${product.id}`}
                        checked={form.products_available.includes(product.id)}
                        onCheckedChange={() => toggleProduct(product.id)}
                      />
                      <label htmlFor={`prod-${product.id}`} className="flex-1 text-sm cursor-pointer">
                        {product.name}
                        <span className="text-slate-500 ml-2">{formatCurrency(product.price)}</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">
                <strong>Early Bird Bonus:</strong> All pre-orders automatically include a free web-sized watermarked digital image!
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-amber-500 hover:bg-amber-600">
                {editingEvent ? 'Update' : 'Create'} Event
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Link Gallery Dialog */}
      <Dialog open={showLinkGalleryDialog} onOpenChange={(open) => {
        setShowLinkGalleryDialog(open);
        if (!open) {
          setSelectedEventForGallery(null);
          setSelectedGalleryIds([]);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Link Galleries to Event</DialogTitle>
            <DialogDescription>
              Select galleries to link to "{selectedEventForGallery?.name}". 
              You can select multiple galleries for events with many participants.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-2">
            <p className="text-sm text-slate-600 mb-2">
              Selected: {selectedGalleryIds.length} galleries
            </p>
          </div>
          
          <div className="space-y-1 max-h-80 overflow-y-auto border rounded-lg">
            {galleries.map(gallery => {
              const isSelected = selectedGalleryIds.includes(gallery.id);
              return (
                <div
                  key={gallery.id}
                  className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 ${
                    isSelected ? 'bg-amber-50' : ''
                  }`}
                  onClick={() => toggleGallerySelection(gallery.id)}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleGallerySelection(gallery.id)}
                  />
                  <div className="flex-1">
                    <p className="font-medium">{gallery.name}</p>
                    <p className="text-sm text-slate-500">{gallery.photo_count || 0} photos</p>
                  </div>
                </div>
              );
            })}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkGalleryDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleLinkMultipleGalleries}
              className="bg-amber-500 hover:bg-amber-600"
              disabled={selectedGalleryIds.length === 0}
            >
              Link {selectedGalleryIds.length} {selectedGalleryIds.length === 1 ? 'Gallery' : 'Galleries'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreOrderEventsPage;
